import { _ as _extends } from './extends-a2cb9f0c.esm.js';
import { _ as _objectWithoutPropertiesLoose } from './objectWithoutPropertiesLoose-98be5ae5.esm.js';
import React, { useEffect, useState } from 'react';
import { useApiCall } from '@growth/custom-hooks/useApiCall';
import { isValidLobName } from '@growth/validations';
import eventEmitter from '@growth/dom-event-emitter';
import { GenericErrorBlock } from '@growth/error-card';
import { AnalyticsContextProvider } from '@growth/use-analytics';
import { g as generateCardsSequence } from './renderer-mapper-e4565e17.esm.js';
import { getSkywalkerData, skywalkerResponseFormatter } from '../fetcher/dist/growth-skywalker-fetcher.esm.js';
import { i as isValidServerSkywalkerResponse, E as EVENT_RELOAD_SKYWALKER, p as pdtPushInstance, a as adobePushInstance } from './helpers-fe286f75.esm.js';
import { _ as _taggedTemplateLiteralLoose } from './taggedTemplateLiteralLoose-7a042257.esm.js';
import { FullPageLoaderMweb } from '@growth/loader';
import styled from 'styled-components';
import { pdtPush } from '@growth/pdt-push';
import { S as SkywalkerRootWrapper } from './style-1cded105.esm.js';

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}

var RendererErrorBoundary = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(RendererErrorBoundary, _React$Component);
  function RendererErrorBoundary(props) {
    var _this;
    _this = _React$Component.call(this, props) || this;
    _this.state = {
      hasError: false
    };
    return _this;
  }
  RendererErrorBoundary.getDerivedStateFromError = function getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return {
      hasError: true
    };
  };
  var _proto = RendererErrorBoundary.prototype;
  _proto.componentDidCatch = function componentDidCatch(error, errorInfo) {
    var onError = this.props.onError;
    onError == null || onError(error, errorInfo);
  };
  _proto.render = function render() {
    var hasError = this.state.hasError;
    var children = this.props.children;
    if (hasError) {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
    return children;
  };
  return RendererErrorBoundary;
}(React.Component);
RendererErrorBoundary.defaultProps = {
  onError: undefined
};

var _templateObject;
var RendererLoaderWrapper = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  min-height: ", ";\n"])), function (props) {
  return props.minHeight ? props.minHeight : '600px';
});
var RendererLoader = function RendererLoader(_ref) {
  var minHeight = _ref.minHeight,
    LoaderComponent = _ref.LoaderComponent;
  if (LoaderComponent) {
    return /*#__PURE__*/React.createElement(LoaderComponent, null);
  }
  return /*#__PURE__*/React.createElement(RendererLoaderWrapper, {
    minHeight: minHeight
  }, /*#__PURE__*/React.createElement(FullPageLoaderMweb, {
    top: "100px"
  }));
};
RendererLoader.defaultProps = {
  minHeight: '600px',
  LoaderComponent: undefined
};

var CardRenderer = function CardRenderer(_ref) {
  var cardRender = _ref.cardRender,
    rootStyle = _ref.rootStyle,
    debug = _ref.debug;
  useEffect(function () {
    if (cardRender.length) {
      var keys = '';
      cardRender.forEach(function (item) {
        if (item && item.key) keys += item.key + "|";
      });
      pdtPush("skywalker-card-rendered", {
        event_detail: {
          event_type: "load_event",
          event_value: "" + keys
        }
      });
    }
  }, [cardRender]);
  if (cardRender.length) {
    return /*#__PURE__*/React.createElement(SkywalkerRootWrapper, {
      style: rootStyle,
      debug: debug
    }, cardRender);
  }
  return null;
};

var getCardWithSeq = function getCardWithSeq(_ref) {
  var formatedData = _ref.formatedData,
    customRendererCollection = _ref.customRendererCollection,
    rendererCollection = _ref.rendererCollection;
  var _ref2 = formatedData || {},
    _ref2$sequence = _ref2.sequence,
    sequence = _ref2$sequence === void 0 ? [] : _ref2$sequence,
    _ref2$data = _ref2.data,
    data = _ref2$data === void 0 ? [] : _ref2$data,
    _ref2$globalData = _ref2.globalData,
    globalData = _ref2$globalData === void 0 ? {} : _ref2$globalData;
  if (data && sequence.length > 0) {
    var cardsWithSeq = generateCardsSequence({
      skywalkerSequence: sequence,
      skywalkerData: data,
      globalData: globalData,
      rendererCollection: rendererCollection,
      customRendererCollection: customRendererCollection
    });
    return cardsWithSeq;
  }
  return [];
};
var RenderSkywalkerClient = function RenderSkywalkerClient(_ref3) {
  var debug = _ref3.debug,
    lobName = _ref3.lobName,
    page = _ref3.page,
    customRendererCollection = _ref3.customRendererCollection,
    skywakerDataServerProp = _ref3.skywalkerData,
    rendererCollection = _ref3.rendererCollection,
    pdtInstance = _ref3.pdtInstance,
    adobeInstance = _ref3.adobeInstance,
    errorCardStyle = _ref3.errorCardStyle,
    fetcherConfig = _ref3.fetcherConfig,
    rootStyle = _ref3.rootStyle,
    cardProps = _ref3.cardProps,
    shouldShowError = _ref3.shouldShowError,
    extraReqBody = _ref3.extraReqBody,
    loaderComponent = _ref3.loaderComponent;
  if (!isValidLobName(lobName)) {
    throw new Error(lobName + " is not a valid lobName for RenderSkywalkerClient");
  }
  var _useApiCall = useApiCall(getSkywalkerData, {
      fetcherConfig: fetcherConfig,
      extraReqBody: extraReqBody
    }),
    skywalkerData = _useApiCall[0],
    getData = _useApiCall[1];
  var data = skywalkerData.data,
    error = skywalkerData.error,
    completed = skywalkerData.completed,
    loading = skywalkerData.loading;

  /**
   * 1 fetcher (client only)
   */
  var fetchDataOnClient = function fetchDataOnClient() {
    var bodyReqParams = {
      lobName: lobName,
      page: page
    };
    getData(bodyReqParams);
  };
  var reloadSkywalker = function reloadSkywalker() {
    fetchDataOnClient();
  };
  var handleErrorBlockLoad = function handleErrorBlockLoad() {};
  var handleComponentError = function handleComponentError(cardError, errorInfo) {};
  useEffect(function () {
    /** skywakerDataServerProp -- Server side props */
    if (!isValidServerSkywalkerResponse(skywakerDataServerProp)) {
      fetchDataOnClient();
    }
  }, [lobName, page, skywakerDataServerProp]);

  /**
   * 2 card mapper
   */
  var cardDataAndSequence = {
    globalData: {},
    cardItems: []
  };

  /** SSR: if data is present from server side props, use it as initial states to setCards */
  if (skywakerDataServerProp && isValidServerSkywalkerResponse(skywakerDataServerProp)) {
    var formatedData = skywalkerResponseFormatter(skywakerDataServerProp);
    cardDataAndSequence = getCardWithSeq({
      formatedData: formatedData,
      rendererCollection: rendererCollection,
      customRendererCollection: customRendererCollection
    });
  }
  var _useState = useState(cardDataAndSequence),
    cards = _useState[0],
    setCards = _useState[1];
  var cardItems = cards.cardItems,
    globalData = cards.globalData;
  useEffect(function () {
    if (isValidServerSkywalkerResponse(skywakerDataServerProp)) return;

    /** set cards for client side rendring in absence of server prop */
    if (data) {
      var c = getCardWithSeq({
        formatedData: data,
        rendererCollection: rendererCollection,
        customRendererCollection: customRendererCollection
      });
      setCards(c);
    }
  }, [data, customRendererCollection, skywakerDataServerProp, rendererCollection]);

  /**
   * Observers
   */

  useEffect(function () {
    /**
     * Get the new fresh data from skywalker in following cases:
     * - if user has new recent search
     * - if user log-in or logout
     */
    eventEmitter.on(EVENT_RELOAD_SKYWALKER, function () {
      reloadSkywalker();
    });
    return function () {
      return eventEmitter.off(EVENT_RELOAD_SKYWALKER, function () {
        reloadSkywalker();
      });
    };
  }, []);
  useEffect(function () {
    var _globalData$GI_ANALYT;
    /** Send all experiments details to analytics */
    var analyticsData = globalData == null || (_globalData$GI_ANALYT = globalData.GI_ANALYTICS_DATA) == null ? void 0 : _globalData$GI_ANALYT.data;
    if (analyticsData) {
      var loadedComponents = '';
      cardItems.forEach(function (item) {
        loadedComponents = "" + (loadedComponents ? loadedComponents + "|" : '') + item.backend.metadata.seq.id;
      });
      adobeInstance == null || adobeInstance.track('ctaClick', {
        account: {
          accountInfo: {
            hydraSegment: analyticsData == null ? void 0 : analyticsData.hydraSegmentList,
            loyaltyStatus: analyticsData == null ? void 0 : analyticsData.loyaltyTier
          }
        },
        page: {
          pageInfo: {
            loadedComponents: loadedComponents
          }
        },
        cta: {}
      });
    }
  }, [globalData, lobName, page, cardItems]);

  /**
   * 3 Renderer
   */
  if (completed && error) {
    /** we don't show the error cards by default.
     * Error cards on lobs page would not fit well with other lob cards. */
    if (debug || !shouldShowError) {
      console.error(error);
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    }
    return /*#__PURE__*/React.createElement(GenericErrorBlock, {
      style: errorCardStyle,
      onReloadClick: reloadSkywalker,
      onLoad: handleErrorBlockLoad
    });
  }

  /** If skywalker is loading, show the loader */
  if (loading || !skywakerDataServerProp && !completed) {
    return /*#__PURE__*/React.createElement(RendererLoader, {
      LoaderComponent: loaderComponent
    });
  }

  /** render the card once api loads and we have cardItem */
  if ((cardItems == null ? void 0 : cardItems.length) > 0) {
    if (debug) {
      console.log('cardRender', cards);
    }
    var cardRender = cardItems.map(function (c, index) {
      var _c$backend;
      var SkywalkerCard = c.component;
      var cardData = (c == null || (_c$backend = c.backend) == null ? void 0 : _c$backend.data) || {};
      var cardPosition = index;
      if (!SkywalkerCard || Object.keys(cardData).length === 0) return null;
      return /*#__PURE__*/React.createElement("section", {
        key: c.backend.metadata.seq.id,
        "data-id": c.backend.metadata.seq.id
      }, /*#__PURE__*/React.createElement(AnalyticsContextProvider, {
        pdtInstance: pdtInstance,
        adobeInstance: adobeInstance
      }, /*#__PURE__*/React.createElement(RendererErrorBoundary, {
        onError: handleComponentError
      }, /*#__PURE__*/React.createElement(SkywalkerCard, {
        cardSeq: c.backend.metadata.seq,
        lobName: lobName,
        page: page,
        fetcherConfig: fetcherConfig,
        skywalkerCardData: c.backend.data,
        cardProps: cardProps,
        cardPosition: cardPosition
      }))));
    });
    return /*#__PURE__*/React.createElement(CardRenderer, {
      cardRender: cardRender,
      rootStyle: rootStyle,
      debug: debug
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null);
};

var _excluded = ["extraReqBody"];
var SkywalkerRenderEntry = function SkywalkerRenderEntry(_ref) {
  var skywalkerCardComponentMaps = _ref.skywalkerCardComponentMaps,
    fetcherConfig = _ref.fetcherConfig,
    extraReqBody = _ref.extraReqBody,
    rootStyle = _ref.rootStyle;
  var ComponentWithHOC = function ComponentWithHOC(_ref2) {
    var propsExtraReqBody = _ref2.extraReqBody,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded);
    return /*#__PURE__*/React.createElement(RenderSkywalkerClient, _extends({
      rendererCollection: skywalkerCardComponentMaps,
      fetcherConfig: fetcherConfig,
      pdtInstance: pdtPushInstance,
      adobeInstance: adobePushInstance,
      rootStyle: rootStyle
    }, props, {
      extraReqBody: _extends({}, extraReqBody, propsExtraReqBody)
    }));
  };
  return ComponentWithHOC;
};

export { SkywalkerRenderEntry as S };
